import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { MetricsRow, SearchLinks, ProgressBar } from '../shared'

function EbayListings() {
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState(null)
  const [statusCounts, setStatusCounts] = useState({})
  const [syncState, setSyncState] = useState(null)
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' })
  const abortControllerRef = useRef(null)

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    search: '',
    status: 'all'
  })

  // Display options
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [limit, setLimit] = useState(100)

  useEffect(() => {
    loadSyncState()
  }, [])

  async function loadSyncState() {
    try {
      const response = await api.get('/api/channels/ebay/sync-state')
      setSyncState(response)
      setStatusCounts(response.statusCounts || {})
    } catch (err) {
      console.error('Failed to load sync state:', err)
    }
  }

  async function handleCheckNewListings() {
    setSyncing(true)
    try {
      const result = await api.post('/api/channels/ebay/check-new-listings')
      const newCount = result.newListings?.length || result.count || 0
      alert(`Found ${newCount} new listing(s) from eBay.`)
      loadSyncState()
      if (hasSearched) loadListings()
    } catch (err) {
      alert(`Error checking for new listings: ${err.message}`)
    } finally {
      setSyncing(false)
    }
  }

  const loadListings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (searchFilters.search) params.set('search', searchFilters.search)
      if (searchFilters.status && searchFilters.status !== 'all') params.set('status', searchFilters.status)
      params.set('limit', '500')

      const response = await api.get(`/api/channels/ebay/live-listings?${params}`)
      setListings(response.listings || [])
      setStatusCounts(response.statusCounts || {})
      setSelectedItems(new Set())
    } catch (err) {
      console.error('Failed to load eBay listings:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    setHasSearched(true)
    loadListings()
  }

  function clearFilters() {
    setSearchFilters({ search: '', status: 'all' })
    setSortBy('updated_at')
    setSortOrder('desc')
    setLimit(100)
  }

  // Client-side sorting and pagination
  const displayItems = useMemo(() => {
    const sorted = [...listings].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (['price', 'quantity'].includes(sortBy)) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      }

      if (typeof aVal === 'string') aVal = aVal?.toLowerCase() || ''
      if (typeof bVal === 'string') bVal = bVal?.toLowerCase() || ''

      if (['created_at', 'updated_at', 'processed_at'].includes(sortBy)) {
        aVal = new Date(aVal).getTime() || 0
        bVal = new Date(bVal).getTime() || 0
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted.slice(0, limit)
  }, [listings, sortBy, sortOrder, limit])

  function handleSort(field) {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  function SortHeader({ field, children, align }) {
    const isActive = sortBy === field
    const arrow = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', textAlign: align, userSelect: 'none' }}
        className={isActive ? 'sort-active' : ''}
      >
        {children}{arrow}
      </th>
    )
  }

  function handleSelectAll(e) {
    if (e.target.checked) {
      setSelectedItems(new Set(displayItems.map(item => item.id)))
    } else {
      setSelectedItems(new Set())
    }
  }

  function handleSelectItem(id) {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  async function handleProcessSelected() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Process ${selectedItems.size} listing(s)?\n\n` +
      `This will:\n` +
      `• Auto-categorize using AI\n` +
      `• Extract item specifics (Brand, Model, etc.)\n` +
      `• Update the listing on eBay\n\n` +
      `Continue?`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    let errors = []
    let successes = []

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      const item = listings.find(l => l.id === id)
      if (!item) continue

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Processing: ${item.title?.substring(0, 40)}...`
      })

      try {
        const result = await api.post('/api/channels/ebay/process-item', {
          ebay_item_id: item.ebay_item_id,
          listing_id: item.id
        })

        if (result.success) {
          successes.push({
            id: item.id,
            title: item.title,
            category: result.category_name,
            specifics: result.item_specifics_count || 0
          })
        } else {
          errors.push({ id: item.id, title: item.title, error: result.error || 'Unknown error' })
        }
        completed++
      } catch (err) {
        errors.push({ id: item.id, title: item.title, error: err.message })
        completed++
      }

      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    // Log full results to console
    console.log('=== EBAY PROCESSING RESULTS ===')
    console.log(`Total: ${completed}, Success: ${successes.length}, Failed: ${errors.length}`)
    console.table(successes.map(s => ({
      title: s.title?.substring(0, 60),
      category: s.category || '-',
      specifics: s.specifics || 0
    })))
    if (errors.length > 0) {
      console.log('ERRORS:')
      console.table(errors.map(e => ({ title: e.title?.substring(0, 60), error: e.error })))
    }

    // Show summary in alert with first 10 items
    let message = `Processed ${completed} listing(s).\n\n`

    if (successes.length > 0) {
      message += `✓ ${successes.length} SUCCESSFUL:\n`
      message += '─'.repeat(40) + '\n'
      successes.slice(0, 10).forEach(s => {
        message += `\n• ${s.title?.substring(0, 50)}${s.title?.length > 50 ? '...' : ''}\n`
        if (s.category) message += `  Category: ${s.category}\n`
        if (s.specifics) message += `  Item Specifics: ${s.specifics} fields updated\n`
      })
      if (successes.length > 10) {
        message += `\n... and ${successes.length - 10} more (see browser console for full list)\n`
      }
    }

    if (errors.length > 0) {
      message += `\n✗ ${errors.length} FAILED:\n`
      message += '─'.repeat(40) + '\n'
      errors.slice(0, 5).forEach(e => {
        message += `\n• ${e.title?.substring(0, 50)}${e.title?.length > 50 ? '...' : ''}\n`
        message += `  Error: ${e.error}\n`
      })
      if (errors.length > 5) {
        message += `\n... and ${errors.length - 5} more errors\n`
      }
    }

    message += '\n📋 Full results logged to browser console (F12 → Console)'

    alert(message)
    loadListings()
  }

  function handleCancelOperation() {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }

  async function handleBulkDelete() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Delete ${selectedItems.size} listing(s) from the database?\n\n` +
      `This only removes them from Prism's tracking list, not from eBay.\n\n` +
      `Continue?`
    )
    if (!confirmed) return

    setProcessing(true)
    try {
      await api.post('/api/channels/ebay/live-listings/bulk-delete', {
        ids: Array.from(selectedItems)
      })
      alert(`Deleted ${selectedItems.size} listing(s)`)
      setSelectedItems(new Set())
      loadListings()
      loadSyncState()
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handleBulkStatusChange(newStatus) {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Change status of ${selectedItems.size} listing(s) to "${newStatus}"?`
    )
    if (!confirmed) return

    setProcessing(true)
    try {
      await api.post('/api/channels/ebay/live-listings/bulk-update', {
        ids: Array.from(selectedItems),
        status: newStatus
      })
      alert(`Updated ${selectedItems.size} listing(s) to ${newStatus}`)
      setSelectedItems(new Set())
      loadListings()
      loadSyncState()
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  function getStatusBadge(status) {
    const colors = {
      active: 'green',
      pending: 'amber',
      pushed: 'blue',
      error: 'red',
      skipped: 'neutral',
      ended: 'gray'
    }
    return (
      <span className={`badge badge-${colors[status] || 'neutral'}`}>
        {status}
      </span>
    )
  }

  const pendingCount = listings.filter(l => l.status === 'pending').length
  const totalCount = Object.values(statusCounts).reduce((a, b) => a + b, 0)

  const metrics = [
    { label: 'Total', value: totalCount, color: 'blue' },
    { label: 'Pending', value: statusCounts.pending || 0, color: 'amber' },
    { label: 'Pushed', value: statusCounts.pushed || 0, color: 'green' },
    { label: 'Skipped', value: statusCounts.skipped || 0, color: 'neutral' }
  ]

  return (
    <div>
      <MetricsRow metrics={metrics} />

      {/* Sync Status */}
      <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <div>
            <span className="text-sm" style={{ fontWeight: 500 }}>Last Sync: </span>
            <span className="text-sm text-muted">
              {syncState?.lastSync
                ? `${new Date(syncState.lastSync).toLocaleString('en-GB')} (${syncState.listingsFetched || 0} fetched)`
                : 'Never'
              }
            </span>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCheckNewListings}
            disabled={syncing}
          >
            {syncing ? 'Checking...' : 'Check for New Listings'}
          </button>
        </div>
      </div>

      {processing && (
        <ProgressBar
          current={progress.current}
          total={progress.total}
          status={progress.status}
          onCancel={handleCancelOperation}
        />
      )}

      <form onSubmit={handleSearch}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <div className="filter-section">
            <div className="filter-group filter-group-wide">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by title, SKU, or eBay Item ID..."
                value={searchFilters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                className="form-input"
                value={searchFilters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="pushed">Pushed</option>
                <option value="skipped">Skipped</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>

          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </button>
            {listings.length > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                Showing {displayItems.length} of {listings.length} listings
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Bulk Actions */}
      {displayItems.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <span className="text-sm" style={{ fontWeight: 500, color: 'var(--color-primary)' }}>
              {selectedItems.size} selected
            </span>
            <button
              className="btn btn-primary"
              onClick={handleProcessSelected}
              disabled={selectedItems.size === 0 || processing}
            >
              Process Selected
            </button>
            <select
              className="form-input"
              style={{ width: 'auto' }}
              value=""
              onChange={e => e.target.value && handleBulkStatusChange(e.target.value)}
              disabled={selectedItems.size === 0 || processing}
            >
              <option value="">Change Status...</option>
              <option value="pending">Pending</option>
              <option value="pushed">Pushed</option>
              <option value="skipped">Skipped</option>
              <option value="error">Error</option>
            </select>
            <button
              className="btn btn-secondary"
              onClick={handleBulkDelete}
              disabled={selectedItems.size === 0 || processing}
              style={{ color: 'var(--color-error)' }}
            >
              Delete Selected
            </button>
            {pendingCount > 0 && (
              <span className="text-sm text-muted">
                {pendingCount} pending listings need processing
              </span>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search eBay Listings</div>
            <div className="empty-state-description">Use the filters above and click "Search" to find eBay listings.</div>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Listings Found</div>
            <div className="empty-state-description">Try adjusting your search filters.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.size === displayItems.length && displayItems.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <SortHeader field="title">Title</SortHeader>
                <th>SKU</th>
                <th>eBay Item ID</th>
                <SortHeader field="price" align="right">Price</SortHeader>
                <SortHeader field="quantity" align="center">Qty</SortHeader>
                <th>Category</th>
                <SortHeader field="status">Status</SortHeader>
                <SortHeader field="updated_at">Updated</SortHeader>
                <th style={{ width: 120 }}>Search</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(item => (
                <tr key={item.id} className={selectedItems.has(item.id) ? 'row-selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title}
                    </div>
                  </td>
                  <td className="text-sm text-muted">{item.sku || '—'}</td>
                  <td className="text-sm">
                    {item.ebay_item_id ? (
                      <a
                        href={`https://www.ebay.co.uk/itm/${item.ebay_item_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontFamily: 'monospace' }}
                      >
                        {item.ebay_item_id}
                      </a>
                    ) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.price)}</td>
                  <td style={{ textAlign: 'center' }}>{item.quantity || '—'}</td>
                  <td className="text-sm text-muted">{item.category_id || '—'}</td>
                  <td>{getStatusBadge(item.status)}</td>
                  <td className="text-sm text-muted">
                    {item.updated_at ? new Date(item.updated_at).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td><SearchLinks title={item.title} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Per-page selector */}
        {displayItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {displayItems.length} of {listings.length} loaded
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm text-muted">Per page:</span>
              {[25, 50, 100, 200].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLimit(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EbayListings
